import { Settings } from '../types';
import { apiService } from './apiService';
import { getStoredAuthToken } from '../utils/authToken';
import { BASE_URL } from '../config/apiConfig';
import { isTauri, tauriWrapper } from '../utils/isTauri';
import { isAndroid, getClientPlatform } from '../utils/environment';
import { STRUCTURED_CORRECTION_PROMPT, parseStructuredCorrection, type StructuredCorrectionResult } from './correctionParser';

// Android's correction popup is a fresh WebView per show() (see CorrectionPopup.tsx),
// so this singleton is re-created from scratch on every popup open and has to wait on
// a full network round-trip before it knows which model to use - noticeably slow on a
// mobile connection. This cache lets a freshly-opened popup start from the last known-good
// config instantly (hydrateFromAndroidCache) while the real fetch still runs in the
// background to correct it if it's changed. Desktop/iOS never touch this key.
const ANDROID_AI_SETTINGS_CACHE_KEY = 'kalimat_android_ai_settings_cache';

export class AIService {
  private static instance: AIService;
  private aiSettings: Settings = {
    // Initialize with empty values - they will be populated from the backend
    ollamaApiUrl: '',
    model: '',
    hotkey: 'CommandOrControl+Space',
    apiKey: undefined,
    prompts: {
      correction: `You are a helpful assistant. Correct the following text for grammar, spelling, and punctuation.
- CRITICAL: Preserve ALL markdown syntax (e.g., **, *, _, >, #, -, etc.) exactly as it appears in the input. Do NOT remove or modify markdown formatting.
- CRITICAL: Do NOT remove leading characters like **, *, _, >, #, or any other markdown symbols at the beginning of the text.
- Detect the language of the input text and keep the output in the SAME language.
- Do NOT translate the text to another language.
- Only output the corrected text, without quotes, commentary, or explanation.
- Preserve the original meaning and tone.
- Do NOT modify phone numbers, email addresses, URLs, dates, times, or other specific data formats. Keep them exactly as they appear in the input.

Text:
{text}`,
      translation: 'Translate the following text into {language}. Only output the translation, without quotes or commentary.\n{text}',
      questionAnswering: `You are a helpful assistant. You will be given a Text (between triple backticks) and a user Question.
- Always base your response ONLY on the provided Text.
- If the Question asks you to translate the Text into a language L (e.g., 'write it in Arabic/Dutch/...' or 'translate to L'), output the full translation into L.
- If the Question asks to rewrite, rephrase, shorten, summarize, explain, extract info, or change tone/style, perform that transformation using ONLY the provided Text.
- Otherwise, answer succinctly about the Text.
- Output ONLY the final answer with no quotes or commentary.

Text:
\`\`\`
{text}
\`\`\`
Question: {question}`,
      generalChat: `You are a helpful assistant. Have a conversation with the user. Be concise and helpful in your responses.`
    }
  };
  private useCustomAISettings: boolean = false;

  private constructor() {
    // Don't load from localStorage anymore to avoid confusion
  }

  public static getInstance(): AIService {
    if (!AIService.instance) {
      AIService.instance = new AIService();
    }
    return AIService.instance;
  }

  public async initializeWithAISettingsFromBackend(): Promise<void> {
    try {
      console.log('Initializing AI service with backend AI settings from dedicated ai_settings table');
      // Fetch AI settings from backend (uses ai_settings table exclusively)
      const response = await apiService.getAISettings();
      console.log('Received AI settings response from ai_settings table:', response);
      
      // Check if we have configurations in the response
      if (response.configurations && response.configurations.length > 0) {
        // Find the active configuration
        const activeConfig = response.configurations.find(
          (config: any) => config.id === response.activeConfigId
        ) || response.configurations[0]; // Fall back to first config if no active found
        
        if (activeConfig) {
          // Update settings with the active configuration
          this.aiSettings = {
            ...this.aiSettings,
            ollamaApiUrl: activeConfig.apiUrl || this.aiSettings.ollamaApiUrl,
            model: activeConfig.model || this.aiSettings.model,
            apiKey: activeConfig.apiKey || this.aiSettings.apiKey,
            prompts: {
              ...this.aiSettings.prompts,
              ...(activeConfig.prompts || {})
            }
          };
          
          this.useCustomAISettings = activeConfig.isCustom === true;

          console.log('Using AI configuration from backend:', {
            isCustom: this.useCustomAISettings,
            apiUrl: this.aiSettings.ollamaApiUrl,
            model: this.aiSettings.model
          });
        }
      } else {
        // If no configs are returned, reset to defaults and mark as not custom
        this.useCustomAISettings = false;
        console.log('No configs found, using default settings and marking as non-custom');
      }

      // Cache regardless of which branch above ran - the far more common case is no custom
      // config at all (useCustomAISettings stays false, aiSettings stays at the constructor
      // defaults), and that "no custom config" state needs to be instantly restorable too,
      // not just a real custom config - otherwise most users would never get a cache hit here.
      this.cacheForAndroid();

      // Not persisted to localStorage in general to avoid confusion (e.g. stale settings
      // surviving a logout) - cacheForAndroid() above is a narrow, Android-only exception.
    } catch (error) {
      console.error('Error initializing with backend AI settings from dedicated ai_settings table:', error);
      // If the error is due to authentication, we can continue with default settings
      if (error instanceof Error && error.message.includes('Access token required')) {
        console.log('User not authenticated, continuing with default AI settings and marking as non-custom');
        this.useCustomAISettings = false; // Default settings are not custom
      } else {
        // For other errors, still log them but continue with defaults
        console.error('Error loading AI settings, using defaults:', error);
        this.useCustomAISettings = false; // Assume default settings on error
      }
    }
  }

  /**
   * Instantly applies the last known-good AI config (written by cacheForAndroid below) so a
   * freshly-opened Android popup doesn't render/act with empty/default settings while the real
   * initializeWithAISettingsFromBackend() fetch is still in flight. No-op on desktop/iOS.
   */
  public hydrateFromAndroidCache(): void {
    if (!isAndroid()) return;
    try {
      const cached = localStorage.getItem(ANDROID_AI_SETTINGS_CACHE_KEY);
      if (!cached) return;
      const parsed = JSON.parse(cached);
      this.aiSettings = { ...this.aiSettings, ...parsed.aiSettings };
      this.useCustomAISettings = !!parsed.useCustomAISettings;
    } catch (error) {
      console.error('Failed to hydrate AI settings from Android cache:', error);
    }
  }

  private cacheForAndroid(): void {
    if (!isAndroid()) return;
    try {
      localStorage.setItem(ANDROID_AI_SETTINGS_CACHE_KEY, JSON.stringify({
        aiSettings: this.aiSettings,
        useCustomAISettings: this.useCustomAISettings,
      }));
    } catch (error) {
      console.error('Failed to cache AI settings for Android:', error);
    }
  }

  public async correctText(text: string, promptOverride?: string): Promise<string> {
    try {
      await this.trackModelUsage();

      // Validate that we have the required settings
      if (!this.aiSettings.ollamaApiUrl || !this.aiSettings.model) {
        throw new Error('AI service not properly configured. Please check your AI service settings in Settings > AI Service Management.');
      }

      // Computed once and reused for every provider branch below (and passed through to the
      // proxy path), so correctTextStructured() can swap in STRUCTURED_CORRECTION_PROMPT
      // without duplicating this method's provider-routing logic.
      const correctionPromptText = this.interpolatePrompt(
        promptOverride || this.aiSettings.prompts?.correction || 'Correct the following text, only output the corrected text, and keep it in the same language:\n{text}',
        { text }
      );

      // If using default AI configuration, route through backend proxy for security
      if (!this.useCustomAISettings) {
        return await this.makeProxyRequest('correction', { text }, promptOverride);
      }

      // Custom AI configuration - make direct request from frontend
      const isOpenAI = this.aiSettings.ollamaApiUrl.includes('openai');
      const isAnthropic = this.aiSettings.ollamaApiUrl.includes('anthropic');
      const isMistral = this.aiSettings.ollamaApiUrl.includes('mistral');
      const isGoogle = this.aiSettings.ollamaApiUrl.includes('generativelanguage.googleapis.com');
      const isOpenRouter = this.aiSettings.ollamaApiUrl.includes('openrouter.ai');
      const isOllama = (this.aiSettings.ollamaApiUrl.includes('/api/') || this.aiSettings.ollamaApiUrl.includes('ollama')) && !isOpenRouter;
      const isLMStudio = this.aiSettings.ollamaApiUrl.includes('lm-studio') || this.aiSettings.ollamaApiUrl.includes('localhost:1234');
      const isCustom = !(isOpenAI || isAnthropic || isMistral || isGoogle || isOllama || isLMStudio || isOpenRouter);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add API key if needed
      if (this.aiSettings.apiKey) {
        if (isOpenAI) {
          headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
        } else if (isAnthropic) {
          headers['x-api-key'] = this.aiSettings.apiKey;
          headers['anthropic-version'] = '2023-06-01';
        } else if (isGoogle) {
          headers['x-goog-api-key'] = this.aiSettings.apiKey;
        } else {
          // For other providers, try common header names
          headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
        }
      }
      
      let body;
      if (isOpenAI || isMistral || isLMStudio || isCustom) {
        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [
            {
              role: "user",
              content: correctionPromptText
            }
          ],
          stream: false,
        });
      } else if (isAnthropic) {
        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [
            {
              role: "user",
              content: correctionPromptText
            }
          ],
          stream: false,
          max_tokens: 1024,
        });
      } else if (isGoogle) {
        body = JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: correctionPromptText
                }
              ]
            }
          ]
        });
      } else {
        // Ollama or custom provider using chat format
        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [
            {
              role: "user",
              content: correctionPromptText
            }
          ],
          stream: false,
        });
      }

      // For Google API, use the specific endpoint
      let fetchUrl = this.aiSettings.ollamaApiUrl;
      if (isGoogle && this.aiSettings.apiKey) {
        fetchUrl = `https://generativelanguage.googleapis.com/v1beta/models/${this.aiSettings.model || 'gemini-pro'}:generateContent?key=${this.aiSettings.apiKey}`;
      } else if (isOllama) {
        // For Ollama, construct the full URL from base URL
        const baseUrl = this.aiSettings.ollamaApiUrl.replace(/\/api\/(generate|chat|tags).*$/, '');
        fetchUrl = `${baseUrl}/api/chat`; // Use /api/chat for better conversation handling
      }

      // In Tauri production builds, direct webview fetch to a local Ollama instance can fail
      // (often surfaced only as "Failed to fetch"). For Ollama and LM Studio endpoints, route via the Tauri
      // backend to avoid CORS/network restrictions.
      if ((isOllama || isLMStudio) && isTauri()) {
        try {
          const prompt = correctionPromptText;
          console.log('[AIService] Invoking Tauri backend for Ollama/LM Studio:', {
            url: fetchUrl,
            model: this.aiSettings.model,
            prompt: prompt.substring(0, 100) + '...'
          });
          const command = isLMStudio ? 'generate_text_lmstudio_with_url' : 'generate_text_with_url';
          const responseText = await tauriWrapper.invoke(command, {
            prompt,
            model: this.aiSettings.model,
            url: fetchUrl,
          });
          return typeof responseText === 'string' ? responseText : text;
        } catch (error) {
          console.error('Tauri Ollama/LM Studio invocation failed:', {
            url: fetchUrl,
            model: this.aiSettings.model,
            error,
            errorMessage: error instanceof Error ? error.message : String(error),
            errorStack: error instanceof Error ? error.stack : undefined,
          });
          const errorMessage = error instanceof Error
            ? error.message
            : (typeof error === 'object' && error !== null && 'error' in error ? String(error.error) : String(error));
          throw new Error(`Error correcting text: ${errorMessage}`);
        }
      }

      let response: Response;
      try {
        response = await fetch(fetchUrl, {
          method: 'POST',
          headers,
          body,
        });
      } catch (error) {
        console.error('[AIService] Network error during correctText fetch', {
          url: fetchUrl,
          origin,
          isTauri: isTauri(),
          provider: isOpenAI ? 'OpenAI' : isAnthropic ? 'Anthropic' : isMistral ? 'Mistral' : isGoogle ? 'Google' : isOllama ? 'Ollama' : isLMStudio ? 'LM Studio' : 'Custom',
          model: this.aiSettings.model,
          error,
        });
        const providerName = isOpenAI ? 'OpenAI' : isAnthropic ? 'Anthropic' : isMistral ? 'Mistral' : isGoogle ? 'Google' : isOllama ? 'Ollama' : isLMStudio ? 'LM Studio' : 'Custom';
        throw error instanceof Error
          ? new Error(`Failed to fetch (${providerName}) at ${fetchUrl} from ${origin}: ${error.message}`)
          : new Error(`Failed to fetch (${providerName}) at ${fetchUrl} from ${origin}`);
      }

      if (!response.ok) {
        let bodyPreview: string | null = null;
        try {
          bodyPreview = (await response.text()).slice(0, 800);
        } catch {
          bodyPreview = null;
        }
        console.error('[AIService] Non-OK response during correctText fetch', {
          url: fetchUrl,
          origin,
          isTauri: isTauri(),
          provider: isOpenAI ? 'OpenAI' : isAnthropic ? 'Anthropic' : isMistral ? 'Mistral' : isGoogle ? 'Google' : isOllama ? 'Ollama' : isLMStudio ? 'LM Studio' : 'Custom',
          model: this.aiSettings.model,
          status: response.status,
          statusText: response.statusText,
          bodyPreview,
        });
        if (response.status === 404) {
          throw new Error('AI service endpoint not found (404). Please check your AI service configuration in Settings > AI Service Management.');
        } else if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized access to AI service. Please check your API key in Settings > AI Service Management.');
        } else {
          throw new Error(`AI service request failed with status ${response.status}. Please check your AI service configuration.`);
        }
      }

      // Check if response is actually JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON response received from AI service:', textResponse.substring(0, 200) + '...');
        throw new Error('AI service returned unexpected response format. Please check your AI service configuration.');
      }

      const data = await response.json();
      
      // Extract response based on provider
      if (isOpenAI || isMistral || isLMStudio || isCustom) {
        return data.choices?.[0]?.message?.content || text;
      } else if (isAnthropic) {
        return data.content?.[0]?.text || text;
      } else if (isGoogle) {
        return data.candidates?.[0]?.content?.parts?.[0]?.text || text;
      } else {
        // Ollama or similar
        return data.response || text;
      }
    } catch (error) {
      console.error('Error correcting text:', error);
      // Re-throw the error with a more user-friendly message
      if (error instanceof Error) {
        throw new Error(`Error correcting text: ${error.message}`);
      } else {
        throw new Error('An unknown error occurred while correcting text');
      }
    }
  }

  /**
   * Structured correction: asks the model for a list of discrete edits
   * ({original, replacement, reason, category}) instead of one rewritten blob,
   * so the UI can show per-fix accept/reject (see CorrectionDiffView) instead of
   * an all-or-nothing replace. Reuses correctText()'s existing provider routing
   * (proxy / OpenAI / Anthropic / Google / Ollama / LM Studio / custom) via
   * promptOverride - no separate network path to maintain.
   *
   * Always non-streaming: partial JSON can't be rendered incrementally, so unlike
   * correctTextStream() there is no onChunk callback here.
   *
   * On parse failure (e.g. a custom provider that ignores the JSON instruction),
   * falls back to a single edit that replaces the whole text, so callers can
   * still render *something* via CorrectionDiffView rather than erroring out.
   */
  public async correctTextStructured(text: string): Promise<StructuredCorrectionResult> {
    const raw = await this.correctText(text, STRUCTURED_CORRECTION_PROMPT);
    try {
      return parseStructuredCorrection(raw, text);
    } catch (parseError) {
      console.warn('[AIService] Structured correction parse failed, falling back to single-edit result:', parseError);
      const suggestion = raw.trim();
      if (!suggestion || suggestion === text.trim()) {
        return { clean: true, edits: [] };
      }
      return {
        clean: false,
        edits: [{ original: text, replacement: suggestion, reason: 'AI suggestion', category: 'grammar' }],
      };
    }
  }

  /**
   * Streaming correction via backend SSE proxy.
   * Supports both default and custom AI configurations.
   * onChunk is called with incremental text as it is received.
   */
  public async correctTextStream(
    text: string,
    onChunk: (partial: string) => void,
    options?: { signal?: AbortSignal }
  ): Promise<string> {
    await this.trackModelUsage();
    if (!this.aiSettings.ollamaApiUrl || !this.aiSettings.model) {
      throw new Error('AI service not properly configured. Please check your AI service settings.');
    }

    const prompt = this.interpolatePrompt(
      this.aiSettings.prompts?.correction ||
        'Correct the following text, only output the corrected text, and keep it in the same language:\n{text}',
      { text }
    );

    // If using custom AI settings (local Ollama/LM Studio), make direct request
    if (this.useCustomAISettings) {
      console.log('[CORRECT STREAM] Using custom AI settings, making direct request');
      return await this.makeDirectStreamingRequest(prompt, onChunk, options);
    }

    console.log('[CORRECT STREAM] Using default AI settings, routing through proxy');
    const url = `${BASE_URL}/api/ai-proxy/proxy-stream`;
    const controller = new AbortController();
    const signal = options?.signal || controller.signal;

    let accumulated = '';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // token is automatically attached by apiService in regular calls, but here we use direct fetch
        // so we must manually add Authorization header
        ...(getStoredAuthToken()
          ? { Authorization: `Bearer ${getStoredAuthToken()}` }
          : {}),
        'x-model-name': this.aiSettings.model || '',
      },
      body: JSON.stringify({ operation: 'correction', prompt }),
      signal,
    });

    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => '');
      throw new Error(`Streaming request failed: ${response.status} ${text}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    // Carries any trailing partial line across reader.read() calls, since a
    // single SSE event can be split across two network reads.
    let sseBuffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      // Server sends SSE lines like: data: {"chunk":"..."}\n\n
      const lines = sseBuffer.split('\n');
      sseBuffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const payload = trimmed.slice(5);
        if (!payload) continue;
        if (payload.trim() === '[DONE]') continue;
        try {
          const obj = JSON.parse(payload);
          if (obj && typeof obj.chunk === 'string') {
            // The backend already extracts the content, so use it directly
            // Don't try to parse it again as a provider chunk
            accumulated += obj.chunk;

            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));

            onChunk(accumulated);
          } else {
            // Fallback: raw text
            accumulated += payload;
            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));
            onChunk(accumulated);
          }
        } catch {
          // Not JSON; append raw
          accumulated += payload;
          // Add a small delay to make streaming visible to users
          await new Promise(resolve => setTimeout(resolve, 5));
          onChunk(accumulated);
        }
      }
    }

    const finalResult = accumulated || text;
    console.log('[CORRECT STREAM] Final result:', finalResult);
    return finalResult;
  }

  /**
   * Streaming translation via backend SSE proxy.
   * Supports both default and custom AI configurations.
   * onChunk is called with incremental text as it is received.
   */
  public async translateTextStream(
    text: string,
    targetLanguage: string,
    onChunk: (partial: string) => void,
    options?: { signal?: AbortSignal }
  ): Promise<string> {
    await this.trackModelUsage();
    if (!this.aiSettings.ollamaApiUrl || !this.aiSettings.model) {
      throw new Error('AI service not properly configured. Please check your AI service settings.');
    }

    const prompt = this.interpolatePrompt(
      this.aiSettings.prompts?.translation ||
        'Translate the following text into {language}. Only output the translation, without quotes or commentary.\n{text}',
      { text, language: targetLanguage }
    );

    // If using custom AI settings (local Ollama/LM Studio), make direct request
    if (this.useCustomAISettings) {
      console.log('[TRANSLATE STREAM] Using custom AI settings, making direct request');
      return await this.makeDirectStreamingRequest(prompt, onChunk, options);
    }

    console.log('[TRANSLATE STREAM] Using default AI settings, routing through proxy');
    const url = `${BASE_URL}/api/ai-proxy/proxy-stream`;
    const controller = new AbortController();
    const signal = options?.signal || controller.signal;

    let accumulated = '';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getStoredAuthToken()
          ? { Authorization: `Bearer ${getStoredAuthToken()}` }
          : {}),
        'x-model-name': this.aiSettings.model || '',
      },
      body: JSON.stringify({ operation: 'translation', prompt }),
      signal,
    });

    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => '');
      throw new Error(`Streaming request failed: ${response.status} ${text}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    // Carries any trailing partial line across reader.read() calls, since a
    // single SSE event can be split across two network reads.
    let sseBuffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      const lines = sseBuffer.split('\n');
      sseBuffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const payload = trimmed.slice(5);
        if (!payload) continue;
        if (payload.trim() === '[DONE]') continue;
        try {
          const obj = JSON.parse(payload);
          if (obj && typeof obj.chunk === 'string') {
            // The backend already extracts the content, so use it directly
            accumulated += obj.chunk;

            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));

            onChunk(accumulated);
          } else {
            accumulated += payload;
            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));
            onChunk(accumulated);
          }
        } catch {
          accumulated += payload;
          // Add a small delay to make streaming visible to users
          await new Promise(resolve => setTimeout(resolve, 5));
          onChunk(accumulated);
        }
      }
    }

    return accumulated || text;
  }

  /**
   * Streaming question answering via backend SSE proxy.
   * Supports both default and custom AI configurations.
   * onChunk is called with incremental text as it is received.
   */
  public async askQuestionStream(
    text: string,
    question: string,
    onChunk: (partial: string) => void,
    options?: { signal?: AbortSignal; images?: string[] | null }
  ): Promise<string> {
    await this.trackModelUsage();
    if (!this.aiSettings.ollamaApiUrl || !this.aiSettings.model) {
      throw new Error('AI service not properly configured. Please check your AI service settings.');
    }

    const prompt = this.interpolatePrompt(
      this.aiSettings.prompts?.questionAnswering ||
        `You are a helpful assistant. You will be given a Text (between triple backticks) and a user Question.
- Always base your response ONLY on the provided Text.
- If the Question asks you to translate the Text into a language L (e.g., 'write it in Arabic/Dutch/...' or 'translate to L'), output the full translation into L.
- If the Question asks to rewrite, rephrase, shorten, summarize, explain, extract info, or change tone/style, perform that transformation using ONLY the provided Text.
- Otherwise, answer succinctly about the Text.
- Output ONLY the final answer with no quotes or commentary.

Text:
\`\`\`
{text}
\`\`\`
Question: {question}`,
      { text, question }
    );

    // If using custom AI settings (local Ollama/LM Studio), make direct request
    if (this.useCustomAISettings) {
      console.log('[ASK STREAM] Using custom AI settings, making direct request');
      
      // If images are present, we should use makeDirectChatStreamingRequest as it supports images
      if (options?.images && options.images.length > 0) {
        const messages = [
          { role: 'user', content: prompt, image: options.images[0] }
        ];
        return await this.makeDirectChatStreamingRequest(messages, onChunk, options);
      }
      
      return await this.makeDirectStreamingRequest(prompt, onChunk, options);
    }

    console.log('[ASK STREAM] Using default AI settings, routing through proxy');
    const url = `${BASE_URL}/api/ai-proxy/proxy-stream`;
    const controller = new AbortController();
    const signal = options?.signal || controller.signal;

    let accumulated = '';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getStoredAuthToken()
          ? { Authorization: `Bearer ${getStoredAuthToken()}` }
          : {}),
        'x-model-name': this.aiSettings.model || '',
      },
      body: JSON.stringify({ 
        operation: 'questionAnswering', 
        prompt,
        // Include first image if present for proxy support
        ...(options?.images && options.images.length > 0 ? { images: [options.images[0]] } : {})
      }),
      signal,
    });

    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => '');
      throw new Error(`Streaming request failed: ${response.status} ${text}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    // Carries any trailing partial line across reader.read() calls, since a
    // single SSE event can be split across two network reads.
    let sseBuffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      const lines = sseBuffer.split('\n');
      sseBuffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const payload = trimmed.slice(5);
        if (!payload) continue;
        if (payload.trim() === '[DONE]') continue;
        try {
          const obj = JSON.parse(payload);
          if (obj && typeof obj.chunk === 'string') {
            // The backend already extracts the content, so use it directly
            accumulated += obj.chunk;

            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));

            onChunk(accumulated);
          } else {
            accumulated += payload;
            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));
            onChunk(accumulated);
          }
        } catch {
          accumulated += payload;
          // Add a small delay to make streaming visible to users
          await new Promise(resolve => setTimeout(resolve, 5));
          onChunk(accumulated);
        }
      }
    }

    return accumulated || '';
  }

  public async translateText(text: string, targetLanguage: string): Promise<string> {
    try {
      await this.trackModelUsage();
      
      // Validate that we have the required settings
      if (!this.aiSettings.ollamaApiUrl || !this.aiSettings.model) {
        throw new Error('AI service not properly configured. Please check your AI service settings in Settings > AI Service Management.');
      }
      
      // If using default AI configuration, route through backend proxy for security
      if (!this.useCustomAISettings) {
        return await this.makeProxyRequest('translation', { text, language: targetLanguage });
      }

      // Custom AI configuration - make direct request from frontend
      // const isOllama = this.aiSettings.ollamaApiUrl.includes('ollama');
      const isOpenAI = this.aiSettings.ollamaApiUrl.includes('openai');
      const isAnthropic = this.aiSettings.ollamaApiUrl.includes('anthropic');
      const isMistral = this.aiSettings.ollamaApiUrl.includes('mistral');
      const isGoogle = this.aiSettings.ollamaApiUrl.includes('generativelanguage.googleapis.com');
      const isOllama = this.aiSettings.ollamaApiUrl.includes('/api/') || this.aiSettings.ollamaApiUrl.includes('ollama');
      const isLMStudio = this.aiSettings.ollamaApiUrl.includes('lm-studio') || this.aiSettings.ollamaApiUrl.includes('localhost:1234');
      const isCustom = !(isOpenAI || isAnthropic || isMistral || isGoogle || isOllama || isLMStudio);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add API key if needed
      if (this.aiSettings.apiKey) {
        if (isOpenAI) {
          headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
        } else if (isAnthropic) {
          headers['x-api-key'] = this.aiSettings.apiKey;
          headers['anthropic-version'] = '2023-06-01';
        } else if (isGoogle) {
          headers['x-goog-api-key'] = this.aiSettings.apiKey;
        } else {
          // For other providers, try common header names
          headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
        }
      }
      
      let body;
      if (isOpenAI || isMistral || isLMStudio || isCustom) {
        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [
            {
              role: "user",
              content: this.interpolatePrompt(this.aiSettings.prompts?.translation || 'Translate the following text into {language}. Only output the translation, without quotes or commentary.\n{text}', { text, language: targetLanguage })
            }
          ],
          stream: false,
        });
      } else if (isAnthropic) {
        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [
            {
              role: "user",
              content: this.interpolatePrompt(this.aiSettings.prompts?.translation || 'Translate the following text into {language}. Only output the translation, without quotes or commentary.\n{text}', { text, language: targetLanguage })
            }
          ],
          stream: false,
          max_tokens: 1024,
        });
      } else if (isGoogle) {
        body = JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: this.interpolatePrompt(this.aiSettings.prompts?.translation || 'Translate the following text into {language}. Only output the translation, without quotes or commentary.\n{text}', { text, language: targetLanguage })
                }
              ]
            }
          ]
        });
      } else {
        // Ollama or custom provider using completion format
        body = JSON.stringify({
          model: this.aiSettings.model,
          prompt: this.interpolatePrompt(this.aiSettings.prompts?.translation || 'Translate the following text into {language}. Only output the translation, without quotes or commentary.\n{text}', { text, language: targetLanguage }),
          stream: false,
        });
      }

      // For Google API, use the specific endpoint
      let fetchUrl = this.aiSettings.ollamaApiUrl;
      if (isGoogle && this.aiSettings.apiKey) {
        fetchUrl = `https://generativelanguage.googleapis.com/v1beta/models/${this.aiSettings.model || 'gemini-pro'}:generateContent?key=${this.aiSettings.apiKey}`;
      }

      // In Tauri production builds, direct webview fetch to a local Ollama/LM Studio instance can fail
      // due to CORS/network restrictions. Route via the Tauri backend.
      if ((isOllama || isLMStudio) && isTauri()) {
        try {
          const { invoke } = await import('@tauri-apps/api/core');
          const prompt = this.interpolatePrompt(
            this.aiSettings.prompts?.translation || 'Translate the following text into {language}. Only output the translation, without quotes or commentary.\n{text}',
            { text, language: targetLanguage }
          );
          console.log('[AIService] Invoking Tauri backend for LM Studio translation:', {
            url: fetchUrl,
            model: this.aiSettings.model,
          });
          const command = isLMStudio ? 'generate_text_lmstudio_with_url' : 'generate_text_with_url';
          const responseText = await invoke(command, {
            prompt,
            model: this.aiSettings.model,
            url: fetchUrl,
          });
          return typeof responseText === 'string' ? responseText : text;
        } catch (error) {
          console.error('Tauri LM Studio translation invocation failed:', error);
          throw new Error(`Error translating text: ${error instanceof Error ? error.message : String(error)}`);
        }
      }

      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('AI service endpoint not found (404). Please check your AI service configuration in Settings > AI Service Management.');
        } else if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized access to AI service. Please check your API key in Settings > AI Service Management.');
        } else {
          throw new Error(`AI service request failed with status ${response.status}. Please check your AI service configuration.`);
        }
      }

      // Check if response is actually JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON response received from AI service:', textResponse.substring(0, 200) + '...');
        throw new Error('AI service returned unexpected response format. Please check your AI service configuration.');
      }

      const data = await response.json();
      
      // Extract response based on provider
      if (isOpenAI || isMistral || isLMStudio || isCustom) {
        return data.choices?.[0]?.message?.content || text;
      } else if (isAnthropic) {
        return data.content?.[0]?.text || text;
      } else if (isGoogle) {
        return data.candidates?.[0]?.content?.parts?.[0]?.text || text;
      } else {
        // Ollama or similar
        return data.response || text;
      }
    } catch (error) {
      console.error('Error translating text:', error);
      // Re-throw the error with a more user-friendly message
      if (error instanceof Error) {
        throw new Error(`Error translating text: ${error.message}`);
      } else {
        throw new Error('An unknown error occurred while translating text');
      }
    }
  }

  public async askQuestion(text: string, question: string, options?: { images?: string[] | null }): Promise<string> {
    try {
      await this.trackModelUsage();
      
      // Validate that we have the required settings
      if (!this.aiSettings.ollamaApiUrl || !this.aiSettings.model) {
        throw new Error('AI service not properly configured. Please check your AI service settings in Settings > AI Service Management.');
      }
      
      // If using default AI configuration, route through backend proxy for security
      if (!this.useCustomAISettings) {
        return await this.makeProxyRequest('questionAnswering', { 
          text, 
          question,
          ...(options?.images && options.images.length > 0 ? { images: [options.images[0]] } : {})
        });
      }

      // Custom AI configuration - make direct request from frontend
      // const isOllama = this.aiSettings.ollamaApiUrl.includes('ollama');
      const isOpenAI = this.aiSettings.ollamaApiUrl.includes('openai');
      const isAnthropic = this.aiSettings.ollamaApiUrl.includes('anthropic');
      const isMistral = this.aiSettings.ollamaApiUrl.includes('mistral');
      const isGoogle = this.aiSettings.ollamaApiUrl.includes('generativelanguage.googleapis.com');
      const isOllama = this.aiSettings.ollamaApiUrl.includes('/api/') || this.aiSettings.ollamaApiUrl.includes('ollama');
      const isLMStudio = this.aiSettings.ollamaApiUrl.includes('lm-studio') || this.aiSettings.ollamaApiUrl.includes('localhost:1234');
      const isCustom = !(isOpenAI || isAnthropic || isMistral || isGoogle || isOllama || isLMStudio);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add API key if needed
      if (this.aiSettings.apiKey) {
        if (isOpenAI) {
          headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
        } else if (isAnthropic) {
          headers['x-api-key'] = this.aiSettings.apiKey;
          headers['anthropic-version'] = '2023-06-01';
        } else if (isGoogle) {
          headers['x-goog-api-key'] = this.aiSettings.apiKey;
        } else {
          // For other providers, try common header names
          headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
        }
      }
      
      let body;
      if (isOpenAI || isMistral || isLMStudio || isCustom) {
        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [
            {
              role: "user",
              content: this.interpolatePrompt(this.aiSettings.prompts?.questionAnswering || `You are a helpful assistant. You will be given a Text (between triple backticks) and a user Question.
- Always base your response ONLY on the provided Text.
- If the Question asks you to translate the Text into a language L (e.g., 'write it in Arabic/Dutch/...' or 'translate to L'), output the full translation into L.
- If the Question asks to rewrite, rephrase, shorten, summarize, explain, extract info, or change tone/style, perform that transformation using ONLY the provided Text.
- Otherwise, answer succinctly about the Text.
- Output ONLY the final answer with no quotes or commentary.

Text:
\`\`\`
{text}
\`\`\`
Question: {question}`, { text, question })
            }
          ],
          stream: false,
        });
      } else if (isAnthropic) {
        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [
            {
              role: "user",
              content: this.interpolatePrompt(this.aiSettings.prompts?.questionAnswering || `You are a helpful assistant. You will be given a Text (between triple backticks) and a user Question.
- Always base your response ONLY on the provided Text.
- If the Question asks you to translate the Text into a language L (e.g., 'write it in Arabic/Dutch/...' or 'translate to L'), output the full translation into L.
- If the Question asks to rewrite, rephrase, shorten, summarize, explain, extract info, or change tone/style, perform that transformation using ONLY the provided Text.
- Otherwise, answer succinctly about the Text.
- Output ONLY the final answer with no quotes or commentary.

Text:
\`\`\`
{text}
\`\`\`
Question: {question}`, { text, question })
            }
          ],
          stream: false,
          max_tokens: 1024,
        });
      } else if (isGoogle) {
        body = JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: this.interpolatePrompt(this.aiSettings.prompts?.questionAnswering || `You are a helpful assistant. You will be given a Text (between triple backticks) and a user Question.
- Always base your response ONLY on the provided Text.
- If the Question asks you to translate the Text into a language L (e.g., 'write it in Arabic/Dutch/...' or 'translate to L'), output the full translation into L.
- If the Question asks to rewrite, rephrase, shorten, summarize, explain, extract info, or change tone/style, perform that transformation using ONLY the provided Text.
- Otherwise, answer succinctly about the Text.
- Output ONLY the final answer with no quotes or commentary.

Text:
\`\`\`
{text}
\`\`\`
Question: {question}`, { text, question })
                }
              ]
            }
          ]
        });
      } else {
        // Ollama or custom provider using completion format
        body = JSON.stringify({
          model: this.aiSettings.model,
          prompt: this.interpolatePrompt(this.aiSettings.prompts?.questionAnswering || `You are a helpful assistant. You will be given a Text (between triple backticks) and a user Question.
- Always base your response ONLY on the provided Text.
- If the Question asks you to translate the Text into a language L (e.g., 'write it in Arabic/Dutch/...' or 'translate to L'), output the full translation into L.
- If the Question asks to rewrite, rephrase, shorten, summarize, explain, extract info, or change tone/style, perform that transformation using ONLY the provided Text.
- Otherwise, answer succinctly about the Text.
- Output ONLY the final answer with no quotes or commentary.

Text:
\`\`\`
{text}
\`\`\`
Question: {question}`, { text, question }),
          stream: false,
        });
      }

      // For Google API, use the specific endpoint
      let fetchUrl = this.aiSettings.ollamaApiUrl;
      if (isGoogle && this.aiSettings.apiKey) {
        fetchUrl = `https://generativelanguage.googleapis.com/v1beta/models/${this.aiSettings.model || 'gemini-pro'}:generateContent?key=${this.aiSettings.apiKey}`;
      }

      // In Tauri production builds, direct webview fetch to a local Ollama/LM Studio instance can fail
      // due to CORS/network restrictions. Route via the Tauri backend.
      if ((isOllama || isLMStudio) && isTauri()) {
        try {
          const { invoke } = await import('@tauri-apps/api/core');
          const prompt = this.interpolatePrompt(
            this.aiSettings.prompts?.questionAnswering || `You are a helpful assistant. You will be given a Text (between triple backticks) and a user Question.
- Always base your response ONLY on the provided Text.
- If the Question asks you to translate the Text into a language L (e.g., 'write it in Arabic/Dutch/...' or 'translate to L'), output the full translation into L.
- If the Question asks to rewrite, rephrase, shorten, summarize, explain, extract info, or change tone/style, perform that transformation using ONLY the provided Text.
- Otherwise, answer succinctly about the Text.
- Output ONLY the final answer with no quotes or commentary.

Text:
\`\`\`
{text}
\`\`\`
Question: {question}`,
            { text, question }
          );
          console.log('[AIService] Invoking Tauri backend for LM Studio question answering:', {
            url: fetchUrl,
            model: this.aiSettings.model,
          });
          const command = isLMStudio ? 'generate_text_lmstudio_with_url' : 'generate_text_with_url';
          const responseText = await invoke(command, {
            prompt,
            model: this.aiSettings.model,
            url: fetchUrl,
          });
          return typeof responseText === 'string' ? responseText : 'No response from AI';
        } catch (error) {
          console.error('Tauri LM Studio question answering invocation failed:', error);
          throw new Error(`Error asking question: ${error instanceof Error ? error.message : String(error)}`);
        }
      }

      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('AI service endpoint not found (404). Please check your AI service configuration in Settings > AI Service Management.');
        } else if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized access to AI service. Please check your API key in Settings > AI Service Management.');
        } else {
          throw new Error(`AI service request failed with status ${response.status}. Please check your AI service configuration.`);
        }
      }

      // Check if response is actually JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON response received from AI service:', textResponse.substring(0, 200) + '...');
        throw new Error('AI service returned unexpected response format. Please check your AI service configuration.');
      }

      const data = await response.json();
      
      // Extract response based on provider
      if (isOpenAI || isMistral || isLMStudio || isCustom) {
        return data.choices?.[0]?.message?.content || 'No response';
      } else if (isAnthropic) {
        return data.content?.[0]?.text || 'No response';
      } else if (isGoogle) {
        return data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response';
      } else {
        // Ollama or similar
        return data.response || 'No response';
      }
    } catch (error) {
      console.error('Error asking question:', error);
      // Re-throw the error with a more user-friendly message
      if (error instanceof Error) {
        throw new Error(`Error asking question: ${error.message}`);
      } else {
        throw new Error('An unknown error occurred while asking question');
      }
    }
  }

  /**
   * Streaming chat via backend SSE proxy.
   * Supports both default and custom AI configurations.
   * onChunk is called with incremental text as it is received.
   */
  public async chatStream(
    messages: { role: string; content: string }[],
    onChunk: (partial: string) => void,
    options?: { signal?: AbortSignal }
  ): Promise<string> {
    await this.trackModelUsage();
    if (!this.aiSettings.ollamaApiUrl || !this.aiSettings.model) {
      throw new Error('AI service not properly configured. Please check your AI service settings.');
    }

    // If using custom AI settings (local Ollama/LM Studio), make direct request
    if (this.useCustomAISettings) {
      console.log('[CHAT STREAM] Using custom AI settings, making direct request');
      return await this.makeDirectChatStreamingRequest(messages, onChunk, options);
    }

    console.log('[CHAT STREAM] Using default AI settings, routing through proxy');
    const url = `${BASE_URL}/api/ai-proxy/proxy-stream`;
    const controller = new AbortController();
    const signal = options?.signal || controller.signal;

    let accumulated = '';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getStoredAuthToken()
          ? { Authorization: `Bearer ${getStoredAuthToken()}` }
          : {}),
        'x-model-name': this.aiSettings.model || '',
      },
      body: JSON.stringify({ operation: 'chat', messages }),
      signal,
    });

    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => '');
      throw new Error(`Streaming request failed: ${response.status} ${text}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    // Carries any trailing partial line across reader.read() calls, since a
    // single SSE event can be split across two network reads.
    let sseBuffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      // Server sends SSE lines like: data: {"chunk":"..."}\n\n
      const lines = sseBuffer.split('\n');
      sseBuffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const payload = trimmed.slice(5);
        if (!payload) continue;
        if (payload.trim() === '[DONE]') continue;
        try {
          const obj = JSON.parse(payload);
          if (obj && typeof obj.chunk === 'string') {
            // The backend already extracts the content, so use it directly
            // Don't try to parse it again as a provider chunk
            accumulated += obj.chunk;

            // Add a small delay to make streaming visible to users
            // This improves UX by showing the AI is actively working
            await new Promise(resolve => setTimeout(resolve, 5));

            onChunk(accumulated);
          } else {
            // Fallback: raw text
            accumulated += payload;
            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));
            onChunk(accumulated);
          }
        } catch {
          // Not JSON; append raw
          accumulated += payload;
          // Add a small delay to make streaming visible to users
          await new Promise(resolve => setTimeout(resolve, 5));
          onChunk(accumulated);
        }
      }
    }

    return accumulated || '';
  }

  public async chat(messages: {role: string, content: string}[]): Promise<string> {
    try {
      await this.trackModelUsage();
      
      // Validate that we have the required settings
      if (!this.aiSettings.ollamaApiUrl || !this.aiSettings.model) {
        throw new Error('AI service not properly configured. Please check your AI service settings in Settings > AI Service Management.');
      }
      
      // If using default AI configuration, route through backend proxy for security
      if (!this.useCustomAISettings) {
        return await this.makeProxyRequest('chat', { messages });
      }

      // Custom AI configuration - make direct request from frontend
      // const isOllama = this.aiSettings.ollamaApiUrl.includes('ollama');
      const isOpenAI = this.aiSettings.ollamaApiUrl.includes('openai');
      const isAnthropic = this.aiSettings.ollamaApiUrl.includes('anthropic');
      const isMistral = this.aiSettings.ollamaApiUrl.includes('mistral');
      const isGoogle = this.aiSettings.ollamaApiUrl.includes('generativelanguage.googleapis.com');
      const isOllama = this.aiSettings.ollamaApiUrl.includes('/api/') || this.aiSettings.ollamaApiUrl.includes('ollama');
      const isLMStudio = this.aiSettings.ollamaApiUrl.includes('lm-studio') || this.aiSettings.ollamaApiUrl.includes('localhost:1234');
      const isCustom = !(isOpenAI || isAnthropic || isMistral || isGoogle || isOllama || isLMStudio);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add API key if needed
      if (this.aiSettings.apiKey) {
        if (isOpenAI) {
          headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
        } else if (isAnthropic) {
          headers['x-api-key'] = this.aiSettings.apiKey;
          headers['anthropic-version'] = '2023-06-01';
        } else if (isGoogle) {
          headers['x-goog-api-key'] = this.aiSettings.apiKey;
        } else {
          // For other providers, try common header names
          headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
        }
      }
      
      let body;
      if (isOpenAI || isMistral || isLMStudio || isCustom) {
        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [
            {
              role: "system",
              content: this.aiSettings.prompts?.generalChat || "You are a helpful assistant. Have a conversation with the user. Be concise and helpful in your responses."
            },
            ...messages
          ],
          stream: false,
        });
      } else if (isAnthropic) {
        // Prepare messages for Anthropic format
        const anthropicMessages = messages.map(msg => ({
          role: msg.role === 'assistant' ? 'assistant' : 'user',
          content: msg.content
        }));

        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [
            {
              role: "user",
              content: this.aiSettings.prompts?.generalChat || "You are a helpful assistant. Have a conversation with the user. Be concise and helpful in your responses."
            },
            ...anthropicMessages
          ],
          stream: false,
          max_tokens: 1024,
        });
      } else if (isGoogle) {
        const googleMessages = messages.map(msg => ({
          role: msg.role,
          parts: [{ text: msg.content }]
        }));

        body = JSON.stringify({
          contents: [
            {
              role: "user",
              parts: [{
                text: this.aiSettings.prompts?.generalChat || "You are a helpful assistant. Have a conversation with the user. Be concise and helpful in your responses."
              }]
            },
            ...googleMessages
          ]
        });
      } else {
        // Ollama or custom provider using completion format
        const formattedMessages = messages.map(msg => `${msg.role}: ${msg.content}`).join('\n');
        body = JSON.stringify({
          model: this.aiSettings.model,
          prompt: `${this.aiSettings.prompts?.generalChat || "You are a helpful assistant. Have a conversation with the user. Be concise and helpful in your responses."}\n\n${formattedMessages}`,
          stream: false,
        });
      }

      // For Google API, use the specific endpoint
      let fetchUrl = this.aiSettings.ollamaApiUrl;
      if (isGoogle && this.aiSettings.apiKey) {
        fetchUrl = `https://generativelanguage.googleapis.com/v1beta/models/${this.aiSettings.model || 'gemini-pro'}:generateContent?key=${this.aiSettings.apiKey}`;
      }

      // In Tauri production builds, direct webview fetch to a local Ollama/LM Studio instance can fail
      // due to CORS/network restrictions. Route via the Tauri backend.
      if ((isOllama || isLMStudio) && isTauri()) {
        try {
          const { invoke } = await import('@tauri-apps/api/core');
          console.log('[AIService] Invoking Tauri backend for LM Studio chat:', {
            url: fetchUrl,
            model: this.aiSettings.model,
            messagesCount: messages.length
          });
          
          // For chat, we need to format messages properly for the backend
          const formattedPrompt = messages.map(msg => `${msg.role}: ${msg.content}`).join('\n\n');
          const systemPrompt = this.aiSettings.prompts?.generalChat || "You are a helpful assistant. Have a conversation with the user. Be concise and helpful in your responses.";
          const fullPrompt = `${systemPrompt}\n\n${formattedPrompt}`;
          
          const command = isLMStudio ? 'generate_text_lmstudio_with_url' : 'generate_text_with_url';
          const responseText = await invoke(command, {
            prompt: fullPrompt,
            model: this.aiSettings.model,
            url: fetchUrl,
          });
          return typeof responseText === 'string' ? responseText : 'No response';
        } catch (error) {
          console.error('Tauri LM Studio chat invocation failed:', error);
          throw new Error(`Error in chat: ${error instanceof Error ? error.message : String(error)}`);
        }
      }

      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('AI service endpoint not found (404). Please check your AI service configuration in Settings > AI Service Management.');
        } else if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized access to AI service. Please check your API key in Settings > AI Service Management.');
        } else {
          throw new Error(`AI service request failed with status ${response.status}. Please check your AI service configuration.`);
        }
      }

      // Check if response is actually JSON before parsing
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON response received from AI service:', textResponse.substring(0, 200) + '...');
        throw new Error('AI service returned unexpected response format. Please check your AI service configuration.');
      }

      const data = await response.json();
      
      // Extract response based on provider
      if (isOpenAI || isMistral || isLMStudio || isCustom) {
        return data.choices?.[0]?.message?.content || 'No response';
      } else if (isAnthropic) {
        return data.content?.[0]?.text || 'No response';
      } else if (isGoogle) {
        return data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response';
      } else {
        // Ollama or similar
        return data.response || 'No response';
      }
    } catch (error) {
      console.error('Error in chat:', error);
      // Re-throw the error with a more user-friendly message
      if (error instanceof Error) {
        throw new Error(`Error in chat: ${error.message}`);
      } else {
        throw new Error('An unknown error occurred during chat');
      }
    }
  }

  // Getter methods for AI settings (not general settings)
  public getAISettings(): Settings {
    return { ...this.aiSettings };
  }

  public getUseCustomAISettings(): boolean {
    return this.useCustomAISettings;
  }

  public getModel(): string {
    return this.aiSettings.model;
  }

  public getApiKey(): string | undefined {
    return this.aiSettings.apiKey;
  }

  public getApiUrl(): string {
    return this.aiSettings.ollamaApiUrl;
  }

  public getPrompts() {
    return { ...this.aiSettings.prompts };
  }

  // Setter methods that update backend AI settings table
  public async updateAISettingsInBackend(newSettings: Partial<Settings>): Promise<void> {
    console.log('Updating AI settings in dedicated ai_settings table with:', newSettings);
    this.aiSettings = { ...this.aiSettings, ...newSettings };
    
    // Sync with backend (uses ai_settings table exclusively)
    try {
      // We don't know which configuration ID to update, so we'll skip this for now
      // This method is mainly for keeping the aiService in sync with changes
      console.log('AI settings updated in aiService memory (not persisted to database)');
    } catch (error) {
      console.error('Error updating AI settings in backend dedicated ai_settings table:', error);
      throw error;
    }
  }

  public async setUseCustomAISettings(useCustom: boolean): Promise<void> {
    this.useCustomAISettings = useCustom;
    
    // Sync with backend (uses ai_settings table exclusively)
    try {
      // Note: We're not updating the settings here because we don't know which
      // configuration ID to update. The settings are managed through the UI.
      console.log('AI settings useCustom flag updated in aiService memory (not persisted to database)');
    } catch (error) {
      console.error('Error updating AI settings useCustom flag in backend dedicated ai_settings table:', error);
      throw error;
    }
  }

  public async setActiveConfiguration(config: any): Promise<void> {
    console.log('Setting active AI configuration:', config);
    
    // Update settings with the selected configuration
    this.aiSettings = {
      ...this.aiSettings,
      ollamaApiUrl: config.apiUrl || this.aiSettings.ollamaApiUrl,
      model: config.model || this.aiSettings.model,
      apiKey: config.apiKey || this.aiSettings.apiKey,
      prompts: {
        ...this.aiSettings.prompts,
        ...(config.prompts || {})
      }
    };
    
    console.log('AI service updated with selected configuration:', this.aiSettings);
  }

  public async getAvailableModels(): Promise<string[]> {
    try {
      // Determine provider from current API URL
      const apiUrl = this.aiSettings.ollamaApiUrl;
      const isOpenAI = apiUrl.includes('openai');
      const isAnthropic = apiUrl.includes('anthropic');
      const isGoogle = apiUrl.includes('generativelanguage.googleapis.com');
      const isOllama = !(isOpenAI || isAnthropic || isGoogle);
      
      // For Google, return predefined list since their API doesn't expose available models in the same way
      if (isGoogle) {
        return ['gemini-2.5-flash', 'gemini-1.5-pro', 'gemini-1.5-flash'];
      }
      
      // For Ollama, try to fetch actual models
      if (isOllama) {
        try {
          // If in Tauri, use backend to avoid CORS issues
          if (isTauri()) {
            console.log('[AIService] Fetching Ollama models via Tauri backend');
            const models = await tauriWrapper.invoke('get_ollama_models', { url: apiUrl });
            return Array.isArray(models) ? models : ['gemma:2b', 'llama2', 'mistral', 'phi'];
          } else {
            // Fallback to direct fetch for development
            const baseUrl = apiUrl.split('/api/generate')[0];
            const baseUrl2 = apiUrl.split('/api/chat')[0];
            const finalBaseUrl = apiUrl.includes('/api/generate') ? baseUrl : (apiUrl.includes('/api/chat') ? baseUrl2 : baseUrl);
          const response = await fetch(`${finalBaseUrl}/api/tags`);
          
          if (!response.ok) {
            console.error('Error fetching Ollama models:', response.status, response.statusText);
            // Fallback to predefined list
            return ['gemma:2b', 'llama2', 'mistral', 'phi'];
          }
          
          // Check if response is actually JSON before parsing
          const contentType = response.headers.get('content-type');
          if (!contentType || !contentType.includes('application/json')) {
            console.error('Non-JSON response received when fetching Ollama models');
            // Fallback to predefined list
            return ['gemma:2b', 'llama2', 'mistral', 'phi'];
          }
          
          const data = await response.json();
            return data.models?.map((model: any) => model.name) || [];
          }
        } catch (error) {
          console.error('Error fetching Ollama models:', error);
          // Fallback to predefined list
          return ['gemma:2b', 'llama2', 'mistral', 'phi'];
        }
      }
      
      // For other providers, return predefined lists
      if (isOpenAI) {
        return ['gpt-3.5-turbo', 'gpt-4', 'gpt-4-turbo'];
      } else if (isAnthropic) {
        return ['claude-3-haiku-20240307', 'claude-3-sonnet-20240229', 'claude-3-opus-20240229'];
      } else if (isGoogle) {
        return ['gemini-2.5-flash', 'gemini-1.5-pro', 'gemini-1.5-flash'];
      } else {
        // Default to Ollama models
        return ['gemma:2b', 'llama2', 'mistral', 'phi'];
      }
    } catch (error) {
      console.error('Error fetching available models:', error);
      return [];
    }
  }

  private interpolatePrompt(prompt: string, params: Record<string, string>): string {
    let interpolated = prompt;
    for (const [key, value] of Object.entries(params)) {
      interpolated = interpolated.replace(new RegExp(`{${key}}`, 'g'), value);
    }
    return interpolated;
  }

  /**
   * Make a direct streaming request to custom AI configuration (local Ollama/LM Studio)
   * This bypasses the backend proxy and connects directly to the user's local AI service
   */
  private async makeDirectStreamingRequest(
    prompt: string,
    onChunk: (partial: string) => void,
    options?: { signal?: AbortSignal }
  ): Promise<string> {
    const isOpenAI = this.aiSettings.ollamaApiUrl.includes('openai');
    const isAnthropic = this.aiSettings.ollamaApiUrl.includes('anthropic');
    const isMistral = this.aiSettings.ollamaApiUrl.includes('mistral');
    const isGoogle = this.aiSettings.ollamaApiUrl.includes('generativelanguage.googleapis.com');
    const isOpenRouter = this.aiSettings.ollamaApiUrl.includes('openrouter.ai');
    const isOllama = (this.aiSettings.ollamaApiUrl.includes('/api/') || this.aiSettings.ollamaApiUrl.includes('ollama')) && !isOpenRouter;
    const isLMStudio = this.aiSettings.ollamaApiUrl.includes('lm-studio') || this.aiSettings.ollamaApiUrl.includes('localhost:1234');
    const isCustom = !(isOpenAI || isAnthropic || isMistral || isGoogle || isOllama || isLMStudio || isOpenRouter);

    console.log('[DIRECT STREAMING] Provider detection:', {
      url: this.aiSettings.ollamaApiUrl,
      model: this.aiSettings.model,
      isOpenAI,
      isAnthropic,
      isMistral,
      isGoogle,
      isOpenRouter,
      isOllama,
      isLMStudio,
      isCustom
    });

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add API key if needed
    if (this.aiSettings.apiKey) {
      if (isOpenAI) {
        headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
      } else if (isAnthropic) {
        headers['x-api-key'] = this.aiSettings.apiKey;
        headers['anthropic-version'] = '2023-06-01';
      } else if (isGoogle) {
        headers['x-goog-api-key'] = this.aiSettings.apiKey;
      } else {
        headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
      }
    }

    // Prepare request body with streaming enabled
    let body;
    if (isOpenAI || isMistral || isLMStudio || isCustom || isOpenRouter) {
      body = JSON.stringify({
        model: this.aiSettings.model,
        messages: [{ role: "user", content: prompt }],
        stream: true,
      });
    } else if (isAnthropic) {
      body = JSON.stringify({
        model: this.aiSettings.model,
        messages: [{ role: "user", content: prompt }],
        stream: true,
        max_tokens: 1024,
      });
    } else if (isGoogle) {
      // Google Gemini streaming format
      body = JSON.stringify({
        contents: [
          {
            parts: [
              {
                text: prompt
              }
            ]
          }
        ],
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 1024,
        },
        safetySettings: [
          {
            category: "HARM_CATEGORY_HARASSMENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_HATE_SPEECH",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_DANGEROUS_CONTENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          }
        ]
      });
    } else {
      // Ollama - use chat endpoint with streaming
      body = JSON.stringify({
        model: this.aiSettings.model,
        messages: [{ role: "user", content: prompt }],
        stream: true,
      });
    }

    // Determine fetch URL
    let fetchUrl = this.aiSettings.ollamaApiUrl;
    if (isGoogle && this.aiSettings.apiKey) {
      // Use Gemini streaming endpoint
      fetchUrl = `https://generativelanguage.googleapis.com/v1beta/models/${this.aiSettings.model || 'gemini-pro'}:streamGenerateContent?key=${this.aiSettings.apiKey}`;
    } else if (isOllama) {
      // Convert /api/generate or /api/tags to /api/chat for Ollama
      if (/\/api\/(generate|tags)/.test(fetchUrl)) {
        fetchUrl = fetchUrl.replace(/\/api\/(generate|tags).*/, '/api/chat');
      }
    }

    console.log('[DIRECT STREAMING] Making direct request to:', fetchUrl);

    const controller = new AbortController();
    const signal = options?.signal || controller.signal;

    let response;
    try {
      response = await fetch(fetchUrl, {
        method: 'POST',
        headers,
        body,
        signal,
      });
    } catch (fetchError) {
      // If fetch fails (e.g., CORS error with LM Studio), fall back to Tauri invoke for non-streaming
      if (isLMStudio && isTauri()) {
        console.log('[DIRECT STREAMING] Fetch failed (likely CORS), falling back to Tauri invoke for LM Studio');
        try {
          const { invoke } = await import('@tauri-apps/api/core');
          const responseText = await invoke('generate_text_lmstudio_with_url', {
            prompt,
            model: this.aiSettings.model,
            url: fetchUrl,
          });
          const result = typeof responseText === 'string' ? responseText : '';
          onChunk(result);
          return result;
        } catch (invokeError) {
          console.error('[DIRECT STREAMING] Tauri invoke also failed:', invokeError);
          throw new Error(`LM Studio request failed: ${invokeError instanceof Error ? invokeError.message : String(invokeError)}`);
        }
      }
      throw fetchError;
    }

    if (!response.ok || !response.body) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Direct streaming request failed: ${response.status} ${errorText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let accumulated = '';
    let buffer = ''; // Buffer for accumulating partial JSON (Google branch)
    // Carries any trailing partial line across reader.read() calls, since a
    // single SSE event/line can be split across two network reads.
    let lineBuffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      console.log('[DIRECT STREAMING] Raw chunk:', chunk.substring(0, 200));

      // For Google Gemini, the response is a JSON array streamed in chunks
      // We need to accumulate and parse complete JSON objects
      if (isGoogle) {
        buffer += chunk;

        // Try to extract complete JSON objects from the buffer
        // Gemini sends: [{...},{...},{...}]
        // We look for pattern: },{ or }] to find object boundaries
        let searchStart = 0;
        while (searchStart < buffer.length) {
          // Find the next complete JSON object
          const nextObjStart = buffer.indexOf('{', searchStart);
          if (nextObjStart === -1) break;

          // Find matching closing brace
          let braceCount = 0;
          let nextObjEnd = -1;
          for (let i = nextObjStart; i < buffer.length; i++) {
            if (buffer[i] === '{') braceCount++;
            if (buffer[i] === '}') braceCount--;
            if (braceCount === 0) {
              nextObjEnd = i + 1;
              break;
            }
          }

          if (nextObjEnd === -1) {
            // Incomplete object, wait for more data
            break;
          }

          // Extract and parse the complete object
          const jsonStr = buffer.substring(nextObjStart, nextObjEnd);
          console.log('[DIRECT STREAMING] Attempting to parse Gemini object:', jsonStr.substring(0, 150));
          try {
            const data = JSON.parse(jsonStr);
            console.log('[DIRECT STREAMING] Successfully parsed Gemini object');
            if (data.candidates && data.candidates[0] && data.candidates[0].content) {
              const parts = data.candidates[0].content.parts;
              if (parts && parts[0] && parts[0].text) {
                const content = parts[0].text;
                console.log('[DIRECT STREAMING] Extracted text:', content.substring(0, 50));
                accumulated += content;
                await new Promise(resolve => setTimeout(resolve, 50));
                onChunk(accumulated);
              }
            }
          } catch (e) {
            console.error('[DIRECT STREAMING] Failed to parse Gemini object:', e instanceof Error ? e.message : String(e), 'JSON:', jsonStr.substring(0, 200));
          }

          searchStart = nextObjEnd;
        }

        // Keep only unprocessed part of buffer
        if (searchStart > 0) {
          buffer = buffer.substring(searchStart);
        }
      } else {
        // For other providers, use line-by-line parsing
        lineBuffer += chunk;
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() ?? '';
        for (const line of lines) {
          const trimmed = line.trim();

          // Skip empty lines and SSE comments (lines starting with ':')
          if (!trimmed || trimmed.startsWith(':')) {
            continue;
          }

          let content = '';

          // Ollama format (no 'data:' prefix, each line is JSON)
          if (isOllama) {
            try {
              // Skip if line starts with 'data:' (some Ollama instances use SSE format)
              if (trimmed.startsWith('data:')) {
                const payload = trimmed.slice(5);
                if (!payload || payload.trim() === '[DONE]') continue;
                const data = JSON.parse(payload);
                if (data.message && data.message.content) {
                  content = data.message.content;
                } else if (data.response) {
                  content = data.response;
                } else if (data.content) {
                  content = data.content;
                }
              } else {
                // Try to parse as JSON, but handle cases where multiple JSON objects might be in one line
                try {
                  const data = JSON.parse(trimmed);
                  if (data.message && data.message.content) {
                    content = data.message.content;
                  } else if (data.response) {
                    content = data.response;
                  } else if (data.content) {
                    content = data.content;
                  }
                } catch (parseError) {
                  // If single JSON parse fails, try to extract JSON objects from the line
                  const jsonMatches = trimmed.match(/\{[^{}]*\}/g);
                  if (jsonMatches && jsonMatches.length > 0) {
                    for (const jsonStr of jsonMatches) {
                      try {
                        const data = JSON.parse(jsonStr);
                        if (data.message && data.message.content) {
                          content = data.message.content;
                          break;
                        } else if (data.response) {
                          content = data.response;
                          break;
                        } else if (data.content) {
                          content = data.content;
                          break;
                        }
                      } catch (e) {
                        // Skip invalid JSON matches
                        continue;
                      }
                    }
                  }
                }
              }
            } catch (e) {
              console.error('[DIRECT STREAMING] Failed to parse Ollama chunk:', e, 'Chunk:', trimmed.substring(0, 100));
            }
          } else if (trimmed.startsWith('data:')) {
            // OpenAI/Mistral/Anthropic format (with 'data:' prefix)
            const payload = trimmed.slice(5);
            if (!payload || payload.trim() === '[DONE]') continue;

            try {
              const data = JSON.parse(payload);
              if (data.choices && data.choices[0]) {
                // OpenAI/Mistral format
                const delta = data.choices[0].delta || {};
                content = delta.content || delta.reasoning || '';
                
                // Also check reasoning_details for OpenRouter
                if (!content && delta.reasoning_details && delta.reasoning_details.length > 0) {
                  content = delta.reasoning_details.map((detail: any) => detail.text || '').join('');
                }
              } else if (data.delta && data.delta.text) {
                // Anthropic format
                content = data.delta.text;
              } else if (data.content) {
                // Some providers send direct content field
                content = data.content;
              } else if (data.text) {
                // Some providers send direct text field
                content = data.text;
              } else if (data.reasoning) {
                // OpenRouter reasoning field
                content = data.reasoning;
              }
            } catch (e) {
              console.error('[DIRECT STREAMING] Failed to parse provider chunk:', e);
            }
          } else {
            // Try to parse as JSON directly (for custom providers)
            try {
              const data = JSON.parse(trimmed);
              if (data.content) {
                content = data.content;
              } else if (data.text) {
                content = data.text;
              } else if (data.response) {
                content = data.response;
              } else if (data.message && data.message.content) {
                content = data.message.content;
              }
            } catch (e) {
              // Not JSON, might be raw text - append it
              content = trimmed;
            }
          }

          if (content) {
            accumulated += content;
            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));
            onChunk(accumulated);
          }
        }
      }
    }

    const finalResult = accumulated || '';
    console.log('[DIRECT STREAMING] Final result:', finalResult);
    return finalResult;
  }

  /**
   * Make a direct streaming chat request to custom AI configuration (local Ollama/LM Studio)
   * Similar to makeDirectStreamingRequest but handles message arrays instead of prompts
   */
  private async makeDirectChatStreamingRequest(
    messages: { role: string; content: string }[],
    onChunk: (partial: string) => void,
    options?: { signal?: AbortSignal }
  ): Promise<string> {
    const isOpenAI = this.aiSettings.ollamaApiUrl.includes('openai');
    const isAnthropic = this.aiSettings.ollamaApiUrl.includes('anthropic');
    const isMistral = this.aiSettings.ollamaApiUrl.includes('mistral');
    const isGoogle = this.aiSettings.ollamaApiUrl.includes('generativelanguage.googleapis.com');
    const isOpenRouter = this.aiSettings.ollamaApiUrl.includes('openrouter.ai');
    const isOllama = (this.aiSettings.ollamaApiUrl.includes('/api/') || this.aiSettings.ollamaApiUrl.includes('ollama')) && !isOpenRouter;
    const isLMStudio = this.aiSettings.ollamaApiUrl.includes('lm-studio') || this.aiSettings.ollamaApiUrl.includes('localhost:1234');
    const isCustom = !(isOpenAI || isAnthropic || isMistral || isGoogle || isOllama || isLMStudio || isOpenRouter);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add API key if needed
    if (this.aiSettings.apiKey) {
      if (isOpenAI) {
        headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
      } else if (isAnthropic) {
        headers['x-api-key'] = this.aiSettings.apiKey;
        headers['anthropic-version'] = '2023-06-01';
      } else if (isGoogle) {
        headers['x-goog-api-key'] = this.aiSettings.apiKey;
      } else {
        headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
      }
    }

    // Prepare request body with streaming enabled
    let body;
    if (isOpenAI || isMistral || isLMStudio || isCustom || isOpenRouter) {
      // Map messages to support optional images (OpenAI-style content parts)
      const mapped = [
        {
          role: "system",
          content: this.aiSettings.prompts?.generalChat || "You are a helpful assistant."
        },
        ...messages.map((msg: any) => {
          // Preserve 'assistant' role for OpenAI; others can accept it too
          const role = msg.role;
          const hasImage = !!msg.image;
          if (hasImage) {
            const parts: any[] = [];
            if (msg.content) parts.push({ type: 'text', text: msg.content });
            parts.push({ type: 'image_url', image_url: { url: `data:image/png;base64,${msg.image}` } });
            return { role, content: parts };
          }
          return { role, content: msg.content || '' };
        })
      ];
      body = JSON.stringify({
        model: this.aiSettings.model,
        messages: mapped,
        stream: true,
      });
    } else if (isAnthropic) {
      const anthropicMessages = messages.map(msg => ({
        role: msg.role === 'assistant' ? 'assistant' : 'user',
        content: msg.content
      }));
      body = JSON.stringify({
        model: this.aiSettings.model,
        messages: [
          {
            role: "user",
            content: this.aiSettings.prompts?.generalChat || "You are a helpful assistant."
          },
          ...anthropicMessages
        ],
        stream: true,
        max_tokens: 1024,
      });
    } else if (isGoogle) {
      // Google Gemini streaming format for chat with optional inline image
      const googleMessages = messages.map((msg: any) => ({
        role: msg.role === 'assistant' ? 'model' : 'user',
        parts: [
          ...(msg.content ? [{ text: msg.content }] as any[] : []),
          ...(msg.image ? [{ inlineData: { mimeType: 'image/png', data: msg.image } }] as any[] : []),
        ]
      }));
      body = JSON.stringify({
        contents: [
          {
            role: "user",
            parts: [{
              text: this.aiSettings.prompts?.generalChat || "You are a helpful assistant."
            }]
          },
          ...googleMessages
        ],
        generationConfig: {
          temperature: 0.7,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: 1024,
        },
        safetySettings: [
          {
            category: "HARM_CATEGORY_HARASSMENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_HATE_SPEECH",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_DANGEROUS_CONTENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          }
        ]
      });
    } else {
      // Ollama - use chat endpoint with streaming
      const lastWithImage: any = [...messages as any[]].reverse().find((m) => !!m.image);
      body = JSON.stringify({
        model: this.aiSettings.model,
        messages: [
          {
            role: "system",
            content: this.aiSettings.prompts?.generalChat || "You are a helpful assistant."
          },
          ...messages
        ],
        ...(lastWithImage?.image ? { images: [lastWithImage.image] } : {}),
        stream: true,
      });
    }

    // Determine fetch URL
    let fetchUrl = this.aiSettings.ollamaApiUrl;
    if (isGoogle && this.aiSettings.apiKey) {
      fetchUrl = `https://generativelanguage.googleapis.com/v1beta/models/${this.aiSettings.model || 'gemini-pro'}:streamGenerateContent?key=${this.aiSettings.apiKey}`;
    } else if (isOllama) {
      // Convert /api/generate or /api/tags to /api/chat for Ollama
      if (/\/api\/(generate|tags)/.test(fetchUrl)) {
        fetchUrl = fetchUrl.replace(/\/api\/(generate|tags).*/, '/api/chat');
      }
    }

    console.log('[DIRECT CHAT STREAMING] Making direct request to:', fetchUrl);

    const controller = new AbortController();
    const signal = options?.signal || controller.signal;

    const response = await fetch(fetchUrl, {
      method: 'POST',
      headers,
      body,
      signal,
    });

    if (!response.ok || !response.body) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Direct chat streaming request failed: ${response.status} ${errorText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let accumulated = '';
    let buffer = ''; // Buffer for accumulating partial JSON (Google branch)
    // Carries any trailing partial line across reader.read() calls, since a
    // single SSE event/line can be split across two network reads.
    let lineBuffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      console.log('[DIRECT CHAT STREAMING] Raw chunk:', chunk.substring(0, 200));

      // For Google Gemini, the response is a JSON array streamed in chunks
      if (isGoogle) {
        buffer += chunk;
        
        let searchStart = 0;
        while (searchStart < buffer.length) {
          const nextObjStart = buffer.indexOf('{', searchStart);
          if (nextObjStart === -1) break;
          
          let braceCount = 0;
          let nextObjEnd = -1;
          for (let i = nextObjStart; i < buffer.length; i++) {
            if (buffer[i] === '{') braceCount++;
            if (buffer[i] === '}') braceCount--;
            if (braceCount === 0) {
              nextObjEnd = i + 1;
              break;
            }
          }
          
          if (nextObjEnd === -1) break;
          
          const jsonStr = buffer.substring(nextObjStart, nextObjEnd);
          try {
            const data = JSON.parse(jsonStr);
            if (data.candidates && data.candidates[0] && data.candidates[0].content) {
              const parts = data.candidates[0].content.parts;
              if (parts && parts[0] && parts[0].text) {
                const content = parts[0].text;
                accumulated += content;
                await new Promise(resolve => setTimeout(resolve, 50));
                onChunk(accumulated);
              }
            }
          } catch (e) {
            console.error('[DIRECT CHAT STREAMING] Failed to parse Gemini object:', e);
          }
          
          searchStart = nextObjEnd;
        }
        
        if (searchStart > 0) {
          buffer = buffer.substring(searchStart);
        }
      } else {
        // For other providers, use line-by-line parsing
        lineBuffer += chunk;
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() ?? '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          let content = '';

          if (isOllama) {
            try {
              const data = JSON.parse(trimmed);
              if (data.message && data.message.content) {
                content = data.message.content;
              } else if (data.response) {
                content = data.response;
              }
            } catch (e) {
              console.error('[DIRECT CHAT STREAMING] Failed to parse Ollama chunk:', e);
            }
          } else if (trimmed.startsWith('data:')) {
            const payload = trimmed.slice(5);
            if (!payload || payload.trim() === '[DONE]') continue;

            try {
              const data = JSON.parse(payload);
              if (data.choices && data.choices[0]) {
                const delta = data.choices[0].delta || {};
                content = delta.content || delta.reasoning || '';
                
                // Also check reasoning_details for OpenRouter
                if (!content && delta.reasoning_details && delta.reasoning_details.length > 0) {
                  content = delta.reasoning_details.map((detail: any) => detail.text || '').join('');
                }
              } else if (data.delta && data.delta.text) {
                content = data.delta.text;
              } else if (data.reasoning) {
                // OpenRouter reasoning field
                content = data.reasoning;
              }
            } catch (e) {
              console.error('[DIRECT CHAT STREAMING] Failed to parse provider chunk:', e);
            }
          }

          if (content) {
            accumulated += content;
            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));
            onChunk(accumulated);
          }
        }
      }
    }

    return accumulated || '';
  }

  /**
   * Make a request through the backend proxy (used for default AI configuration)
   * This keeps the default AI credentials secure on the backend
   */
  private async makeProxyRequest(operation: string, params: Record<string, any>, promptOverride?: string): Promise<string> {
    try {
      // Get the token from localStorage
      const token = getStoredAuthToken();

      if (!token) {
        throw new Error('Authentication required. Please log in.');
      }

      const baseUrl = BASE_URL;

      // Prepare the prompt based on operation type
      let prompt = '';
      if (operation === 'correction') {
        // promptOverride lets correctTextStructured() reuse this same routing with
        // STRUCTURED_CORRECTION_PROMPT instead of the free-text default.
        prompt = this.interpolatePrompt(
          promptOverride || this.aiSettings.prompts?.correction || 'Correct the following text, only output the corrected text, and keep it in the same language:\n{text}',
          params
        );
      } else if (operation === 'translation') {
        prompt = this.interpolatePrompt(
          this.aiSettings.prompts?.translation || 'Translate the following text into {language}. Only output the translation, without quotes or commentary.\n{text}',
          params
        );
      } else if (operation === 'questionAnswering') {
        prompt = this.interpolatePrompt(
          this.aiSettings.prompts?.questionAnswering || `You are a helpful assistant. You will be given a Text (between triple backticks) and a user Question.
- Always base your response ONLY on the provided Text.
- If the Question asks you to translate the Text into a language L (e.g., 'write it in Arabic/Dutch/...' or 'translate to L'), output the full translation into L.
- If the Question asks to rewrite, rephrase, shorten, summarize, explain, extract info, or change tone/style, perform that transformation using ONLY the provided Text.
- Otherwise, answer succinctly about the Text.
- Output ONLY the final answer with no quotes or commentary.

Text:
\`\`\`
{text}
\`\`\`
Question: {question}`,
          params
        );
      } else if (operation === 'modification') {
        // For modification, the prompt is already provided in params
        prompt = params.prompt;
      } else if (operation === 'chat') {
        // For chat, we'll send messages directly
      }

      // Prepare request body based on operation
      let requestBody: any;
      if (operation === 'chat') {
        requestBody = {
          operation: 'chat',
          messages: params.messages,
          model: this.aiSettings.model,
        };
      } else {
        requestBody = {
          operation: operation,
          prompt: prompt,
          model: this.aiSettings.model,
        };
      }

      const response = await fetch(`${baseUrl}/api/ai-proxy/proxy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 429) {
          throw new Error('Daily usage limit exceeded. Please upgrade your plan or wait until tomorrow.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Request failed with status ${response.status}`);
        }
      }

      const data = await response.json();

      // Extract the response text based on the structure returned by the proxy
      return data.response || data.text || data.content || 'No response';
    } catch (error) {
      console.error('Error making proxy request:', error);
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('An unknown error occurred while processing your request');
      }
    }
  }

  /**
   * Modify text with various options (shorten, lengthen, simplify, change tone, regenerate)
   */
  public async modifyText(text: string, modification: 'regenerate' | 'shorten' | 'lengthen' | 'simplify' | 'formal' | 'casual' | 'professional'): Promise<string> {
    try {
      await this.trackModelUsage();
      
      // Validate that we have the required settings
      if (!this.aiSettings.ollamaApiUrl || !this.aiSettings.model) {
        throw new Error('AI service not properly configured. Please check your AI service settings in Settings > AI Service Management.');
      }
      
      // Build the prompt based on modification type
      const modificationPrompts: Record<string, string> = {
        regenerate: 'Rewrite the following text with different wording while keeping the same meaning, length, and language. Only output the rewritten text:\n{text}',
        shorten: 'Shorten the following text while keeping the main meaning. Make it more concise. Only output the shortened text:\n{text}',
        lengthen: 'Expand the following text with more details and elaboration while keeping the same meaning. Only output the expanded text:\n{text}',
        simplify: 'Simplify the following text to make it easier to understand. Use simpler words and shorter sentences. Only output the simplified text:\n{text}',
        formal: 'Rewrite the following text in a formal tone. Use professional language. Only output the rewritten text:\n{text}',
        casual: 'Rewrite the following text in a casual, conversational tone. Only output the rewritten text:\n{text}',
        professional: 'Rewrite the following text in a professional business tone. Only output the rewritten text:\n{text}'
      };
      
      const prompt = this.interpolatePrompt(
        modificationPrompts[modification] || modificationPrompts.regenerate,
        { text }
      );
      
      // If using default AI configuration, route through backend proxy
      if (!this.useCustomAISettings) {
        return await this.makeProxyRequest('modification', { prompt, modification });
      }

      // Custom AI configuration - make direct request from frontend
      const isOpenAI = this.aiSettings.ollamaApiUrl.includes('openai');
      const isAnthropic = this.aiSettings.ollamaApiUrl.includes('anthropic');
      const isMistral = this.aiSettings.ollamaApiUrl.includes('mistral');
      const isGoogle = this.aiSettings.ollamaApiUrl.includes('generativelanguage.googleapis.com');
      const isOllama = this.aiSettings.ollamaApiUrl.includes('/api/') || this.aiSettings.ollamaApiUrl.includes('ollama');
      const isLMStudio = this.aiSettings.ollamaApiUrl.includes('lm-studio') || this.aiSettings.ollamaApiUrl.includes('localhost:1234');
      const isCustom = !(isOpenAI || isAnthropic || isMistral || isGoogle || isOllama || isLMStudio);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add API key if needed
      if (this.aiSettings.apiKey) {
        if (isOpenAI) {
          headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
        } else if (isAnthropic) {
          headers['x-api-key'] = this.aiSettings.apiKey;
          headers['anthropic-version'] = '2023-06-01';
        } else if (isGoogle) {
          headers['x-goog-api-key'] = this.aiSettings.apiKey;
        } else {
          headers['Authorization'] = `Bearer ${this.aiSettings.apiKey}`;
        }
      }
      
      let body;
      if (isOpenAI || isMistral || isLMStudio || isCustom) {
        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [{ role: "user", content: prompt }],
          stream: false,
        });
      } else if (isAnthropic) {
        body = JSON.stringify({
          model: this.aiSettings.model,
          messages: [{ role: "user", content: prompt }],
          stream: false,
          max_tokens: 1024,
        });
      } else if (isGoogle) {
        body = JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }]
        });
      } else {
        body = JSON.stringify({
          model: this.aiSettings.model,
          prompt: prompt,
          stream: false,
        });
      }

      let fetchUrl = this.aiSettings.ollamaApiUrl;
      if (isGoogle && this.aiSettings.apiKey) {
        fetchUrl = `https://generativelanguage.googleapis.com/v1beta/models/${this.aiSettings.model || 'gemini-pro'}:generateContent?key=${this.aiSettings.apiKey}`;
      } else if (isOllama) {
        const baseUrl = this.aiSettings.ollamaApiUrl.replace(/\/api\/(generate|chat|tags).*$/, '');
        fetchUrl = `${baseUrl}/api/chat`;
      }

      // In Tauri production builds, direct webview fetch to a local Ollama/LM Studio instance can fail
      // due to CORS/network restrictions. Route via the Tauri backend.
      if ((isOllama || isLMStudio) && isTauri()) {
        try {
          const { invoke } = await import('@tauri-apps/api/core');
          console.log('[AIService] Invoking Tauri backend for LM Studio modification:', {
            url: fetchUrl,
            model: this.aiSettings.model,
            modification
          });
          const command = isLMStudio ? 'generate_text_lmstudio_with_url' : 'generate_text_with_url';
          const responseText = await invoke(command, {
            prompt,
            model: this.aiSettings.model,
            url: fetchUrl,
          });
          return typeof responseText === 'string' ? responseText : text;
        } catch (error) {
          console.error('Tauri LM Studio modification invocation failed:', error);
          throw new Error(`Error modifying text: ${error instanceof Error ? error.message : String(error)}`);
        }
      }

      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('AI service endpoint not found (404). Please check your AI service configuration.');
        } else if (response.status === 401 || response.status === 403) {
          throw new Error('Unauthorized access to AI service. Please check your API key.');
        } else {
          throw new Error(`AI service request failed with status ${response.status}.`);
        }
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('AI service returned unexpected response format.');
      }

      const data = await response.json();
      
      if (isOpenAI || isMistral || isLMStudio || isCustom) {
        return data.choices?.[0]?.message?.content || text;
      } else if (isAnthropic) {
        return data.content?.[0]?.text || text;
      } else if (isGoogle) {
        return data.candidates?.[0]?.content?.parts?.[0]?.text || text;
      } else {
        return data.response || text;
      }
    } catch (error) {
      console.error('Error modifying text:', error);
      if (error instanceof Error) {
        throw new Error(`Error modifying text: ${error.message}`);
      } else {
        throw new Error('An unknown error occurred while modifying text');
      }
    }
  }

  /**
   * Streaming text modification via backend SSE proxy.
   * Supports both default and custom AI configurations.
   */
  public async modifyTextStream(
    text: string,
    modification: 'shorten' | 'lengthen' | 'simplify' | 'formal' | 'casual' | 'professional',
    onChunk: (partial: string) => void,
    options?: { signal?: AbortSignal }
  ): Promise<string> {
    await this.trackModelUsage();
    if (!this.aiSettings.ollamaApiUrl || !this.aiSettings.model) {
      throw new Error('AI service not properly configured. Please check your AI service settings.');
    }

    // Build the prompt based on modification type
    const modificationPrompts: Record<string, string> = {
      shorten: 'Shorten the following text while keeping the main meaning. Make it more concise. Only output the shortened text:\n{text}',
      lengthen: 'Expand the following text with more details and elaboration while keeping the same meaning. Only output the expanded text:\n{text}',
      simplify: 'Simplify the following text to make it easier to understand. Use simpler words and shorter sentences. Only output the simplified text:\n{text}',
      formal: 'Rewrite the following text in a formal tone. Use professional language. Only output the rewritten text:\n{text}',
      casual: 'Rewrite the following text in a casual, conversational tone. Only output the rewritten text:\n{text}',
      professional: 'Rewrite the following text in a professional business tone. Only output the rewritten text:\n{text}'
    };

    const prompt = this.interpolatePrompt(
      modificationPrompts[modification] || modificationPrompts.shorten,
      { text }
    );

    // If using custom AI settings (local Ollama/LM Studio), make direct request
    if (this.useCustomAISettings) {
      console.log('[MODIFY STREAM] Using custom AI settings, making direct request');
      return await this.makeDirectStreamingRequest(prompt, onChunk, options);
    }

    console.log('[MODIFY STREAM] Using default AI settings, routing through proxy');
    // Otherwise use backend proxy for default AI configuration
    const url = `${BASE_URL}/api/ai-proxy/proxy-stream`;
    const controller = new AbortController();
    const signal = options?.signal || controller.signal;

    let accumulated = '';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(getStoredAuthToken()
          ? { Authorization: `Bearer ${getStoredAuthToken()}` }
          : {}),
        'x-model-name': this.aiSettings.model || '',
      },
      body: JSON.stringify({ operation: 'modification', prompt, modification }),
      signal,
    });

    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => '');
      throw new Error(`Streaming request failed: ${response.status} ${text}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    // Carries any trailing partial line across reader.read() calls, since a
    // single SSE event can be split across two network reads.
    let sseBuffer = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      sseBuffer += decoder.decode(value, { stream: true });
      // Server sends SSE lines like: data: {"chunk":"..."}\n\n
      const lines = sseBuffer.split('\n');
      sseBuffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data:')) continue;
        const payload = trimmed.slice(5);
        if (!payload) continue;
        if (payload.trim() === '[DONE]') continue;
        try {
          const obj = JSON.parse(payload);
          if (obj && typeof obj.chunk === 'string') {
            // The backend already extracts the content, so use it directly
            accumulated += obj.chunk;
            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));
            onChunk(accumulated);
          } else {
            accumulated += payload;
            // Add a small delay to make streaming visible to users
            await new Promise(resolve => setTimeout(resolve, 5));
            onChunk(accumulated);
          }
        } catch {
          accumulated += payload;
          // Add a small delay to make streaming visible to users
          await new Promise(resolve => setTimeout(resolve, 5));
          onChunk(accumulated);
        }
      }
    }

    return accumulated || '';
  }

  /**
   * Performs OCR on an image to extract text
   * @param imageBase64 The base64 encoded image string
   */
  public async performOCR(imageBase64: string): Promise<string> {
    const prompt = `Extraheer alle tekst uit deze afbeelding. 
- Geef ALLEEN de gevonden tekst terug.
- Voeg geen inleiding, uitleg of extra tekst toe.
- Behoud de originele lay-out en regelafbrekingen waar mogelijk.
- Als er geen tekst gevonden wordt, geef dan [GEEN_TEKST_GEVONDEN] terug.`;

    const messages = [
      { role: 'user', content: prompt, image: imageBase64 }
    ];

    try {
      const response = await this.chat(messages as any);
      const result = response;
      
      if (result === '[GEEN_TEKST_GEVONDEN]') {
        return '';
      }
      
      return result;
    } catch (error) {
      console.error('[OCR] Error during OCR process:', error);
      throw error;
    }
  }

  /**
   * Track model usage for the current user
   */
  private async trackModelUsage(): Promise<void> {
    try {
      console.log('[MODEL TRACKING] Tracking model usage for model:', this.aiSettings.model);
      
      // Get the token from localStorage
      const token = getStoredAuthToken();
      
      if (!token) {
        console.log('[MODEL TRACKING] No authentication token found, skipping usage tracking');
        return;
      }
      
      const baseUrl = BASE_URL;
      const endpoint = baseUrl ? `${baseUrl}/auth/increment-usage` : '/api/auth/increment-usage';

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-model-name': this.aiSettings.model,  // Using lowercase header name
          'X-Client-Platform': getClientPlatform()
        }
      });

      if (!response.ok) {
        // Don't log 404 errors as they're expected if the endpoint doesn't exist
        if (response.status !== 404) {
          console.error('[MODEL TRACKING] Failed to track model usage, status:', response.status);
        }
        return; // Silently fail on tracking errors
      } else {
        console.log('[MODEL TRACKING] Successfully tracked model usage for model:', this.aiSettings.model);
        // Dispatch a custom event to notify other components to refresh usage count
        window.dispatchEvent(new CustomEvent('usageIncremented', { detail: { model: this.aiSettings.model } }));
      }
    } catch (error) {
      console.error('[MODEL TRACKING] Error tracking model usage:', error);
      // Continue execution even if tracking fails
    }
  }
}

// Export a singleton instance
export const aiService = AIService.getInstance();
